import { useEffect, useState, useCallback, useMemo } from "react";
import { toast } from "react-toastify";
import MultiTable from "../../../Common/MUITable";
import AttendanceReportFilters from "./AttendanceReportFilters";
import AttendancePunchHistoryTable from "./AttendancePunchHistoryTable";
// import { ExportViewAttendance } from "./ExportViewAttendance";
import {
    getTodayDate,
    validateAttendanceDateFilters,
} from "./attendanceReportDateUtils";

import {
    GetAttendanceZones,
    GetAttendanceShifts,
    GetActiveDepartments,
    GetWardsByZone,
    ManageAttendance,
} from "../AttendanceApis";

// ─────────────────────────────────────────────────────────────────────────────

const STATUS_STYLES = {
    present: { label: "Present", bg: "#d1fae5", color: "#065f46" },
    absent:  { label: "Absent",  bg: "#fee2e2", color: "#991b1b" },
    late:    { label: "Late",    bg: "#fef3c7", color: "#92400e" },
};

// ─── Helpers ──────────────────────────────────────────────────────────────────
const unwrap = (res) => {
    if (Array.isArray(res)) return res;
    if (Array.isArray(res?.data)) return res.data;
    if (Array.isArray(res?.Data)) return res.Data;
    if (Array.isArray(res?.data?.data)) return res.data.data;
    if (Array.isArray(res?.data?.Data)) return res.data.Data;
    if (Array.isArray(res?.result)) return res.result;
    if (Array.isArray(res?.Result)) return res.Result;
    return [];
};

const formatTime = (raw) => {
    if (!raw) return "—";
    const timePart = String(raw).split(".")[0];
    const [hStr, mStr] = timePart.split(":");
    if (hStr === undefined || mStr === undefined) return raw;
    const h = parseInt(hStr, 10);
    const m = mStr.padStart(2, "0");
    const period = h >= 12 ? "PM" : "AM";
    const h12 = h % 12 === 0 ? 12 : h % 12;
    return `${String(h12).padStart(2, "0")}:${m} ${period}`;
};

const formatDate = (raw) => {
    if (!raw) return "—";
    const d = new Date(raw);
    if (isNaN(d)) return raw;
    return d.toLocaleDateString("en-GB", {
        day: "2-digit",
        month: "short",
        year: "numeric",
    });
};

const getTextValue = (...values) => {
    const resolved = values.find((value) => value !== undefined && value !== null && value !== "");
    return resolved ?? "—";
};

// ─── StatusBadge ──────────────────────────────────────────────────────────────
const StatusBadge = ({ status }) => {
    const key = status?.toLowerCase();
    const style = STATUS_STYLES[key] ?? { label: status ?? "—", bg: "#e5e7eb", color: "#374151" };
    return (
        <span
            style={{
                backgroundColor: style.bg,
                color: style.color,
                padding: "3px 12px",
                borderRadius: "999px",
                fontWeight: 600,
                fontSize: "11px",
                whiteSpace: "nowrap",
            }}
        >
            {style.label}
        </span>
    );
};

// ─── Main Component ───────────────────────────────────────────────────────────
const ViewAttendanceReport = () => {
    // ── Dropdown options ──────────────────────────────────────────────────────
    const [zoneOptions, setZoneOptions]           = useState([]);
    const [wardOptions, setWardOptions]           = useState([]);
    const [shiftOptions, setShiftOptions]         = useState([]);
    const [departmentOptions, setDepartmentOptions] = useState([]);

    // ── Filters ───────────────────────────────────────────────────────────────
    const [filterData, setFilterData] = useState({
        ZoneId:        "",
        WardId:        "",
        EmployeeSearch: "",
        Status:        "",
        ShiftId:       "",
        DepartmentId:  "",   
        FromDate:      getTodayDate(),   // ← today
      ToDate:        getTodayDate(),
    });

    // ── Raw data from API ─────────────────────────────────────────────────────
    const [rawData, setRawData] = useState([]);

    // ── Fetch dropdowns on mount ──────────────────────────────────────────────
    const fetchDropdowns = useCallback(async () => {
        try {
            const [zones, shifts, departments] = await Promise.all([
                GetAttendanceZones(),
                GetAttendanceShifts(),
                GetActiveDepartments(),
            ]);
            setZoneOptions(
                unwrap(zones).filter(
                    (zone) =>
                        zone.isActive !== false &&
                        String(zone.zoneName ?? zone.ZoneName ?? "")
                            .toLowerCase()
                            .includes("delhi")
                )
            );
            setShiftOptions(unwrap(shifts));
            setDepartmentOptions(unwrap(departments));
        } catch (err) {
            console.error("Dropdown fetch error:", err);
        }
    }, []);

    // ── Fetch attendance ──────────────────────────────────────────────────────
    const fetchAttendance = useCallback(async (filters = filterData) => {
        try {
            const activeFilters = Object.fromEntries(
                Object.entries(filters).filter(([, value]) => value !== "" && value !== null && value !== undefined)
            );

            const payload = {
                ...activeFilters,
                PageNumber: 1,
                PageSize: 100,
            };

            const response = await ManageAttendance(payload);
            const data = unwrap(response);
            setRawData(data);
        } catch (err) {
            console.error("Attendance fetch error:", err);
            toast.error("Failed to load attendance data.");
            setRawData([]);
        }
    }, [filterData]);

    useEffect(() => {
        fetchDropdowns();
        fetchAttendance(filterData);
    }, [fetchDropdowns]);

    useEffect(() => {
        if (zoneOptions.length === 0) return;

        setFilterData((previous) => {
            if (previous.ZoneId) return previous;

            const firstZoneId = String(zoneOptions[0].zoneId ?? zoneOptions[0].ZoneId);

            GetWardsByZone(firstZoneId)
                .then((wards) => {
                    setWardOptions(unwrap(wards));
                })
                .catch((error) => {
                    console.error("Ward fetch error:", error);
                    setWardOptions([]);
                });

            return { ...previous, ZoneId: firstZoneId };
        });
    }, [zoneOptions]);

    const attendanceData_filtered = rawData;

    // ── Handlers ──────────────────────────────────────────────────────────────
    const handleChange = async (field, value) => {
        if ((field === "FromDate" || field === "ToDate") && value) {
            const today = getTodayDate();
            if (value > today) {
                toast.error(
                    field === "FromDate"
                        ? "From Date cannot be a future date."
                        : "To Date cannot be a future date."
                );
                return;
            }
        }

        setFilterData((prev) => {
            const updated = { ...prev, [field]: value };
            if (field === "ZoneId") updated.WardId = "";
            return updated;
        });

        if (field === "ZoneId") {
            if (value) {
                try {
                    const wards = await GetWardsByZone(value);
                    setWardOptions(unwrap(wards));
                } catch (err) {
                    console.error("Ward fetch error:", err);
                    setWardOptions([]);
                }
            } else {
                setWardOptions([]);
            }
        }
    };

    const handleApplyFilters = (e) => {
        e.preventDefault();
        const dateValidationMessage = validateAttendanceDateFilters(filterData);
        if (dateValidationMessage) {
            toast.error(dateValidationMessage);
            return;
        }
        fetchAttendance(filterData);
    };

    const handleReset = () => {
        const cleared = {
            ZoneId: "", WardId: "", EmployeeSearch: "", Status: "",
            ShiftId: "", DepartmentId: "", FromDate: "", ToDate: "",
        };
        setFilterData(cleared);
        setWardOptions([]);
        fetchAttendance(cleared);
    };

    const expandTableThStyle = useMemo(
        () => ({
            backgroundColor: "#003366",
            color: "#fff",
            fontWeight: 600,
            padding: "8px",
            textAlign: "center",
            fontSize: "12px",
        }),
        []
    );

    const expandTableTdStyle = useMemo(
        () => ({
            backgroundColor: "#f9fafb",
            padding: "8px",
            textAlign: "center",
            fontSize: "12px",
            border: "1px solid #ddd",
        }),
        []
    );

    // ── Columns for MUITable ──────────────────────────────────────────────────
    const tableColumns = useMemo(
        () => [
            {
                label: "Emp Code",
                width: "100px",
                render: (row) => getTextValue(
                    row.employeeCode,
                    row.empCode,
                    row.EmployeeCode,
                    row.empId,
                    row.EmployeeId,
                    row.employeeId
                ),
            },
            {
                label: "Employee Name",
                width: "140px",
                render: (row) => getTextValue(row.employeeName, row.empName, row.EmployeeName, row.name),
            },
            {
                label: "Designation",
                width: "120px",
                render: (row) => getTextValue(
                    row.designationName,
                    row.DesignationName,
                    row.designation,
                    row.Designation,
                    row.roleName,
                    row.RoleName
                ),
            },
            {
                label: "Department",
                width: "120px",
                render: (row) => getTextValue(
                    row.departmentName,
                    row.DepartmentName,
                    row.department,
                    row.Department,
                    row.operationName,
                    row.OperationName
                ),
            },
            { label: "Master Shift", field: "masterShift", width: "110px" },
            { label: "Actual Shift", field: "actualShift", width: "110px" },
            { label: "Zone", field: "zoneName", width: "90px" },
            { label: "Ward", field: "circleName", width: "90px" },
            { label: "KPI Category", field: "kpiCategoryName", width: "120px" },
            { label: "Device ID", field: "deviceId", width: "100px" },
            { label: "Date", field: "date", width: "100px", render: (row) => formatDate(row.date) },
            { label: "Punch In", field: "punchIn", width: "100px", render: (row) => formatTime(row.punchIn) },
            { label: "Punch Out", field: "punchOut", width: "100px", render: (row) => formatTime(row.punchOut) },
            { label: "Total Time", field: "totalTime", width: "100px" },
            { label: "Status", field: "status", width: "90px", render: (row) => <StatusBadge status={row.status} /> },
        ],
        []
    );

    // ─────────────────────────────────────────────────────────────────────────
    return (
        <>
            <div className="content text-start">
                <div className="page-content">

                    {/* ── Page Header ── */}
                    <div className="d-flex flex-column flex-md-row justify-content-between align-items-start gap-3 mt-3 mb-3">
                        <div className="me-md-3">
                            <h5 className="mb-1 fw-semibold">Push NIC Attendance Report</h5>
                            <p className="text-muted mb-0">
                                View, monitor and analyze employee attendance records, shifts, punch timings,
                                and attendance status across zones and wards.
                            </p>
                        </div>
                        <div className="d-flex flex-wrap gap-2 flex-shrink-0">
                            <button
                                type="button"
                                className="btn btn-primary btn-sm px-3"
                                onClick={() => toast.info("Push Attendance clicked.")}
                            >
                                Push Attendance
                            </button>
                        </div>
                    </div>

                    <AttendanceReportFilters
                        filterData={filterData}
                        zoneOptions={zoneOptions}
                        wardOptions={wardOptions}
                        shiftOptions={shiftOptions}
                        departmentOptions={departmentOptions}
                        onFilterChange={handleChange}
                        onApply={handleApplyFilters}
                        onReset={handleReset}
                    />

                    <div className="card shadow">
                        <div className="card-body p-0">
                            <div className="top-header text-center">
                                <span>Attendance List</span>
                            </div>
                            <div className="table-responsive">
                                <MultiTable
                                    needSRNumber
                                    columns={tableColumns}
                                    rows={attendanceData_filtered}
                                    tableHeight={500}
                                    rowsPerPageOptions={[10, 25, 50, 100]}
                                    hasExpandButton
                                    expandContent={(row) => (
                                        <AttendancePunchHistoryTable
                                            row={row}
                                            subThStyle={expandTableThStyle}
                                            subTdStyle={expandTableTdStyle}
                                        />
                                    )}
                                />
                            </div>
                        </div>
                    </div>

                </div>
            </div>
        </>
    );
};
export default ViewAttendanceReport;